import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Plus, AlertCircle, Calendar as CalendarIcon, ChevronDown, ChevronRight, UserRound } from "lucide-react";
import { Badge, Button, EmptyState, Select, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { useUsers, userName } from "@/lib/use-users";
import { formatDateTime, formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import { TaskDialog } from "@/features/tasks/TaskDialog";
import type { Task } from "@/lib/types";

/**
 * Bucket a task into a time-based group. We compare against `now` (not a
 * memoized "start of day") because anything past now is overdue, and
 * "today" means "due before midnight local". Bucket order is the order
 * the user wants to see them: overdue → today → this week → later → undated.
 */
type Bucket = "overdue" | "today" | "week" | "later" | "undated";

function bucketFor(task: Task, now: Date): Bucket {
  if (!task.due_at) return "undated";
  const due = new Date(task.due_at);
  if (due.getTime() < now.getTime()) return "overdue";
  // End of today, local.
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  if (due <= endOfToday) return "today";
  // End of week (next Sunday 23:59).
  const endOfWeek = new Date(endOfToday);
  endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfToday.getDay()));
  if (due <= endOfWeek) return "week";
  return "later";
}

const BUCKETS: { key: Bucket; label: string; icon: React.ComponentType<{ className?: string }>; tone: string }[] = [
  { key: "overdue", label: "Overdue",   icon: AlertCircle,  tone: "text-rose-400" },
  { key: "today",   label: "Today",     icon: CalendarIcon, tone: "text-amber-300" },
  { key: "week",    label: "This week", icon: CalendarIcon, tone: "text-rose-300" },
  { key: "later",   label: "Later",     icon: CalendarIcon, tone: "text-zinc-400" },
  { key: "undated", label: "No due date", icon: CalendarIcon, tone: "text-zinc-500" },
];

export function TasksPage() {
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  // null = closed; "new" = create form; Task = edit form. Same shape as
  // every other create+edit dialog target across the app.
  const [editing, setEditing] = useState<Task | "new" | null>(null);
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();
  const [collapsed, setCollapsed] = useState<Set<Bucket>>(new Set(["later", "undated"]));
  // "" = everyone's tasks; a user id = just that person's.
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const { users } = useUsers();

  const load = async () => {
    setLoading(true);
    try {
      // Pull EVERY open task in one go so we can group + show all buckets
      // at once. Completed tasks come along when the toggle is on.
      const params: Record<string, string | number | undefined> = {
        limit: 500,
        ...smartListQueryParams(listId, smartFilter),
      };
      if (!showCompleted) params.status = "pending";
      if (assigneeFilter) params.assignee_id = assigneeFilter;
      const d = await api.get<{ tasks: Task[] }>("/tasks", params);
      setTasks(d.tasks);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showCompleted, listId, smartFilter, assigneeFilter]);

  const toggle = async (t: Task) => {
    const next = t.status === "completed" ? "pending" : "completed";
    setTasks((prev) => prev.map((x) => x.id === t.id
      ? { ...x, status: next, completed_at: next === "completed" ? new Date().toISOString() : null }
      : x,
    ));
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setTasks((prev) => prev.map((x) => x.id === t.id ? t : x));
    }
  };

  // Group by bucket in a stable, sorted order.
  const grouped = useMemo(() => {
    const now = new Date();
    const map: Record<Bucket, Task[]> = { overdue: [], today: [], week: [], later: [], undated: [] };
    for (const t of tasks) {
      // Hide completed tasks from the top buckets even when fetched (the
      // toggle just shows them in their own section so they don't pollute
      // "today's plan").
      if (t.status === "completed" && !showCompleted) continue;
      map[bucketFor(t, now)].push(t);
    }
    // Sort each bucket: overdue most-overdue first, others soonest first;
    // undated by created_at (newest first) since there's no due date.
    const byDueAsc  = (a: Task, b: Task) => +new Date(a.due_at!) - +new Date(b.due_at!);
    const byDueDesc = (a: Task, b: Task) => +new Date(b.due_at!) - +new Date(a.due_at!);
    map.overdue.sort(byDueAsc);
    map.today.sort(byDueAsc);
    map.week.sort(byDueAsc);
    map.later.sort(byDueAsc);
    map.undated.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    return map;
  }, [tasks, showCompleted]);

  // Completed tasks live in their own collapsed section. Recently completed first.
  const completed = useMemo(() => {
    if (!showCompleted) return [];
    return tasks
      .filter((t) => t.status === "completed")
      .sort((a, b) => +new Date(b.completed_at ?? b.updated_at) - +new Date(a.completed_at ?? a.updated_at));
  }, [tasks, showCompleted]);

  const totalOpen = grouped.overdue.length + grouped.today.length + grouped.week.length + grouped.later.length + grouped.undated.length;

  const toggleCollapsed = (b: Bucket) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b); else next.add(b);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Tasks</h1>
          <p className="mt-1 text-sm text-zinc-500">{totalOpen} open · {grouped.overdue.length} overdue</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New task</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/30 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900/60">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-rose-500"
          />
          Show completed
        </label>
        <Select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className="h-8 w-auto text-xs">
          <option value="">Everyone</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </Select>
        <div className="flex-1" />
        <SmartListPicker
          listId={listId}
          filter={smartFilter}
          onChange={(lid, f) => setSmartList(lid, f)}
        />
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({length:8}).map((_,i)=><Skeleton key={i} className="h-14"/>)}</div>
      ) : totalOpen === 0 && completed.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="You're all caught up"
          description="Nothing on your plate right now. Add a task to get going."
          action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New task</Button>}
        />
      ) : (
        <div className="space-y-4">
          {BUCKETS.map(({ key, label, icon: Icon, tone }) => {
            const list = grouped[key];
            if (list.length === 0) return null;
            const isCollapsed = collapsed.has(key);
            return (
              <Section
                key={key}
                title={label}
                count={list.length}
                Icon={Icon}
                tone={tone}
                collapsed={isCollapsed}
                onToggle={() => toggleCollapsed(key)}
              >
                {!isCollapsed && (
                  <ul className="divide-y divide-zinc-800/40">
                    {list.map((t) => <TaskRow key={t.id} task={t} assignee={userName(users, t.assignee_id)} onToggle={toggle} onEdit={setEditing} />)}
                  </ul>
                )}
              </Section>
            );
          })}
          {showCompleted && completed.length > 0 && (
            <Section
              title="Completed"
              count={completed.length}
              Icon={CheckSquare}
              tone="text-emerald-400"
              collapsed={collapsed.has("undated") /* reuse tracking — completed defaults open */ ? false : false}
              onToggle={() => { /* completed section stays expanded — no toggle */ }}
            >
              <ul className="divide-y divide-zinc-800/40">
                {completed.slice(0, 50).map((t) => <TaskRow key={t.id} task={t} assignee={userName(users, t.assignee_id)} onToggle={toggle} onEdit={setEditing} />)}
              </ul>
            </Section>
          )}
        </div>
      )}

      <TaskDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={(saved, mode) => {
          // Patch local state in place so the user doesn't see a full
          // refetch flicker after save. Re-bucketing on the next poll
          // catches anything we missed.
          setTasks((prev) => mode === "create"
            ? [saved, ...prev]
            : prev.map((x) => x.id === saved.id ? saved : x),
          );
          setEditing(null);
        }}
        onDeleted={(deleted) => {
          setTasks((prev) => prev.filter((x) => x.id !== deleted.id));
          setEditing(null);
        }}
      />
    </div>
  );
}

function Section({
  title, count, Icon, tone, collapsed, onToggle, children,
}: {
  title: string;
  count: number;
  Icon: React.ComponentType<{ className?: string }>;
  tone: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 border-b border-zinc-800/40 px-4 py-2.5 text-left hover:bg-zinc-900/40"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-zinc-500" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />}
        <Icon className={cn("h-3.5 w-3.5", tone)} />
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">{title}</span>
        <span className="ml-1 text-[10px] tabular-nums text-zinc-500">{count}</span>
      </button>
      {children}
    </div>
  );
}

function TaskRow({ task, assignee, onToggle, onEdit }: {
  task: Task;
  /** Display name of the person responsible, when known. */
  assignee: string | null;
  onToggle: (t: Task) => void;
  onEdit: (t: Task) => void;
}) {
  const overdue = task.due_at && new Date(task.due_at).getTime() < Date.now() && task.status !== "completed";
  // Whole-row click opens the edit dialog. Interactive children (the
  // checkbox, the contact link) stop propagation so they keep their own
  // behaviour. Using role=button on the <li> instead of nesting a real
  // <button> so the contact <Link> can stay inline without invalid
  // interactive-in-interactive markup.
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={() => onEdit(task)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(task); } }}
      className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-zinc-900/40 focus-visible:bg-zinc-900/40 focus-visible:outline-none"
    >
      <input
        type="checkbox"
        checked={task.status === "completed"}
        onChange={() => onToggle(task)}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 h-4 w-4 shrink-0 rounded border-zinc-700 bg-zinc-900 text-rose-500"
      />
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm", task.status === "completed" ? "line-through text-zinc-500" : "text-zinc-100")}>
          {task.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {task.due_at && (
            <span className={cn("inline-flex items-center gap-1", overdue ? "text-rose-400" : "text-zinc-500")}>
              <CalendarIcon className="h-3 w-3" />
              {formatDateTime(task.due_at)}
            </span>
          )}
          {task.priority !== "normal" && (
            <Badge color={task.priority === "urgent" ? "rose" : task.priority === "high" ? "amber" : "zinc"} size="xs">
              {task.priority}
            </Badge>
          )}
          {assignee && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <UserRound className="h-3 w-3" />
              {assignee}
            </span>
          )}
          {task.contact_id && task.contact_name && (
            <Link
              to={`/contacts/${task.contact_id}`}
              state={{ from: "/tasks" }}
              onClick={(e) => e.stopPropagation()}
              className="text-rose-300 hover:underline"
            >
              {task.contact_name}
            </Link>
          )}
        </div>
      </div>
      <span className="shrink-0 text-[10px] text-zinc-500">{formatRelative(task.created_at)}</span>
    </li>
  );
}

